import type { AdminDashboardOutput, SpeedAnalysis } from "@/api/custom_types";
import { useTranslation } from "@/hooks/useTranslation";
import { cn } from "@/lib/utils";
import { SectionCard } from "@/pages/dashboard/components";
import { formatNumber } from "@/utils/tools";
import { Skeleton, Typography } from "@st-marketplace/ui";
import type { FC } from "react";

/** Below this many snipes a bucket's missed % is noise, so it is drawn faded. */
const MIN_SAMPLES = 5;

const ms = (value: number | null | undefined) =>
	value === null || value === undefined ? "-" : `${Math.round(value)} ms`;

const bucketLabel = (from: number, to: number | null) =>
	to === null ? `${from}+` : `${from}-${to}`;

/** Missed % per speed bucket: one bar each, the bucket's sample count under it. */
const SpeedChart: FC<{ title: string; data: SpeedAnalysis }> = ({
	title,
	data,
}) => {
	const { t } = useTranslation();
	const buckets = data.buckets.filter((b) => b.total > 0);
	const peak = Math.max(1, ...buckets.map((b) => b.missed_percent ?? 0));

	return (
		<figure className="flex min-w-0 flex-col gap-3">
			<figcaption className="flex flex-wrap items-baseline justify-between gap-2">
				<Typography variant="body-md" as="span" className="font-semibold">
					{title}
				</Typography>
				<Typography variant="caption" as="span" className="text-secondary-icon">
					{t("admin.dashboard.avgMissedVsSuccess", {
						missed: ms(data.avg_ms_missed),
						success: ms(data.avg_ms_success),
					})}
				</Typography>
			</figcaption>

			{buckets.length === 0 ? (
				<Typography
					variant="body-sm"
					as="p"
					className="flex h-40 items-center justify-center text-secondary-text-100"
				>
					{t("admin.dashboard.noSpeedData")}
				</Typography>
			) : (
				<div className="flex h-40 items-end gap-[2px] border-b border-secondary-border">
					{buckets.map((bucket) => {
						const percent = bucket.missed_percent ?? 0;
						const label = bucketLabel(bucket.from_ms, bucket.to_ms);
						const sparse = bucket.total < MIN_SAMPLES;
						const summary = t("admin.dashboard.bucketTooltip", {
							range: label,
							percent: formatNumber(percent, "percent"),
							missed: bucket.missed,
							total: bucket.total,
						});
						return (
							<div
								key={bucket.from_ms}
								className="group relative flex h-full min-w-0 flex-1 flex-col justify-end"
								aria-label={summary}
								role="img"
							>
								<div
									className={cn(
										"min-h-[2px] rounded-t bg-primary-fill transition-opacity group-hover:opacity-80",
										sparse && "opacity-40",
									)}
									style={{ height: `${(percent / peak) * 100}%` }}
								/>
								<div className="pointer-events-none absolute bottom-full start-1/2 z-10 mb-1 hidden -translate-x-1/2 whitespace-nowrap rounded-md border border-secondary-border bg-background-400 px-2 py-1 text-xs text-secondary-text shadow group-hover:block">
									{summary}
								</div>
							</div>
						);
					})}
				</div>
			)}
			{buckets.length > 0 ? (
				<div className="flex gap-[2px]">
					{buckets.map((bucket) => (
						<span
							key={bucket.from_ms}
							className="min-w-0 flex-1 truncate text-center text-[10px] tabular-nums text-secondary-icon"
						>
							{bucketLabel(bucket.from_ms, bucket.to_ms)}
						</span>
					))}
				</div>
			) : null}
		</figure>
	);
};

export const MissedAnalysisCard: FC<{
	data?: AdminDashboardOutput["missed_analysis"];
	loading: boolean;
}> = ({ data, loading }) => {
	const { t } = useTranslation();

	return (
		<SectionCard
			title={t("admin.dashboard.missedTitle")}
			metric={
				data ? (
					<Typography variant="caption" as="p" className="text-secondary-icon">
						{t("admin.dashboard.missedMetric", {
							missed: data.missed,
							total: data.total,
						})}
					</Typography>
				) : null
			}
		>
			<div className="grid gap-6 p-4 sm:p-6 lg:grid-cols-3">
				{loading || !data ? (
					[0, 1, 2].map((i) => <Skeleton key={i} className="h-48 w-full" />)
				) : (
					<>
						<SpeedChart
							title={t("admin.dashboard.searchSpeed")}
							data={data.search_speed}
						/>
						<SpeedChart
							title={t("admin.dashboard.buySpeed")}
							data={data.buy_speed}
						/>
						<SpeedChart
							title={t("admin.dashboard.listSpeed")}
							data={data.list_speed}
						/>
					</>
				)}
			</div>
			<Typography
				variant="caption"
				as="p"
				className="px-4 pb-4 text-secondary-icon sm:px-6"
			>
				{t("admin.dashboard.missedLegend")}
			</Typography>
		</SectionCard>
	);
};
